import Select from "../../../../../../CommonComponents/select/Select"
import DatetimeInput from "../../../../../../CommonComponents/DatetimeInput"
import { getInvalidRangeErrorMessage, showToastMessage, validateNumberWithoutDecimal } from "../../../../../../../utils/commonFunctions"
import { CustomScheduleModalFormType } from "../../../../../types"
import { calculateTimeBasedOnTimeOption, maxScheduleDuration } from "../../../../../utils"
import { InfoSvg } from "../../../../../../CommonComponents/svgs/Svgs"
import CustomToolTip from "../../../../../../CommonComponents/CustomToolTip"
import DualEditorForm from "../../console/DualEditorForm"
import useConsole from "../../../../../hooks/useConsole"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"


const TimeOptions = [
    { name: 'startTime', label: 'Start Time' },
    { name: 'endTime', label: 'End Time' }
]

const isSameJsonValue = (a: unknown, b: unknown) => {
    try {
        return JSON.stringify(a) === JSON.stringify(b);
    } catch {
        return a === b;
    }
}

interface TelecommandFormProps {
    formType: CustomScheduleModalFormType;
    disabledSpecification: boolean;
    onChange: (data: any) => void;
    remainingDuration: number;
    onDateTimeValidationChange?: (isValid: boolean) => void;
    telecommandData: {
        type: string;
        name: string;
        time: number | null;
        component: string;
        sequenceData: null | { [key: string]: any };
        duration: number | string;
        delay: number | string;
        timeOption: 'startTime' | 'endTime';
        commandStartTime: null | number;
        commandEndTime: null | number
    }
}

const TelecommandForm = ({ formType, disabledSpecification, onChange, telecommandData, remainingDuration, onDateTimeValidationChange }: TelecommandFormProps) => {
    const { busComponentOptions, fetchBusCommands, busCommandOptions } = useConsole();
    const [componentSearch, setComponentSearch] = useState('');
    const [commandSearch, setCommandSearch] = useState('');
    const telecommandDataRef = useRef(telecommandData);
    const onChangeRef = useRef(onChange);

    const filteredComponents = busComponentOptions.filter((item: any) =>
        item?.label?.toLowerCase().includes(componentSearch.toLowerCase())
    );
    const filteredCommands = busCommandOptions.filter((item: any) =>
        item?.label?.toLowerCase().includes(commandSearch.toLowerCase())
    );

    const { type, name, time, component, duration, sequenceData, delay, timeOption } = telecommandData;

    useEffect(() => {
        telecommandDataRef.current = telecommandData;
        onChangeRef.current = onChange;
    }, [telecommandData, onChange]);


    const getSequenceData = (commandName: string) => {
        const command: any = busCommandOptions.find((item: any) => item?.value?.name === commandName)
        return command?.value?.sequenceData
    }
    const getCommandValue = (commandName: string) => {
        const command: any = busCommandOptions.find((item: any) => item?.value?.name === commandName)
        return command?.label
    }

    const onChangeCommandData = useCallback((data: any) => {
        const updatedTelecommandData = { ...telecommandDataRef.current, ...data };
        telecommandDataRef.current = updatedTelecommandData;
        onChangeRef.current({ telecommandData: updatedTelecommandData })
    }, [])

    const editorTelecommandData = useMemo(() => {
        if (!sequenceData) return {};
        return {
            commandName: name,
            commandType: (type || 'Bus').toUpperCase(),
            sequenceData,
        }
    }, [name, sequenceData, type]);

    const selectedEditorTelecommand = sequenceData && name
        ? `${name}:${sequenceData?.commandId ?? ''}`
        : null;

    const syncEditorDataToSchedule = useCallback((data: any) => {
        if (!data || typeof data !== 'object' || !('sequenceData' in data)) return;
        const nextSequenceData = data.sequenceData;
        if (isSameJsonValue(telecommandDataRef.current.sequenceData, nextSequenceData)) return;
        onChangeCommandData({ sequenceData: nextSequenceData })
    }, [onChangeCommandData])

    const handleDurationChange = (value) => {
        if (value === '') { onChangeCommandData({ duration: '' }); return; }
        const num = parseInt(value, 10)
        if (isNaN(num)) return;
        if (remainingDuration && (num > Number(remainingDuration))) {
            showToastMessage('info', getInvalidRangeErrorMessage('Duration', 1, remainingDuration, 'secs'))
            return;
        }
        onChangeCommandData({ duration: num })
    }

    const handleDelayChange = (value) => {
        if (value === '') { onChangeCommandData({ delay: '' }); return; }
        if (Number(value) < 0) return;
        const num = parseInt(value, 10)
        if (isNaN(num)) return;
        const newTime = telecommandData.commandEndTime
        const maxDelaySecs = maxScheduleDuration * 60;
        if (num > maxDelaySecs) {
            showToastMessage('info', getInvalidRangeErrorMessage('Delay', 1, maxDelaySecs, 'secs'))
            return;
        }
        onChangeCommandData({
            'delay': num,
            'time': Math.round(num + Number(newTime))
        })

    }

    // Auto-set time on mount and when dependencies change
    useEffect(() => {
        if ((telecommandData.commandStartTime === null && telecommandData.commandEndTime === null) || formType === 'update-telecommand') return;
        let newTime = 0;
        if (timeOption === 'startTime') {
            newTime = Number(telecommandData.commandStartTime ?? 0) + Number(delay ?? 0);
        } else if (timeOption === 'endTime') {
            newTime = Number(telecommandData.commandEndTime ?? 0) + Number(delay ?? 0);
        }
        if (telecommandData.time !== newTime) {
            onChangeCommandData({ time: newTime });
        }
    }, [timeOption, telecommandData.commandStartTime, telecommandData.commandEndTime, delay, onChangeCommandData])


    return (
        <div className='custom__schedule__modal__form__body'>
            <div className={`form__field__details ${formType === 'update-telecommand' ? 'disabled' : ''}`}>
                <span className="form__field__details__label">Type</span>
                <div className="w-60">
                    <Select id={1} placement="bottom" value={type}
                        onSelect={() => { }}>
                        <Select.Trigger>
                            <Select.Value value={'Bus Command'} placeholder={'Select Type'} />
                        </Select.Trigger>
                        <Select.Content>
                            <Select.Group>
                                <Select.Item value="Bus">Bus Command</Select.Item>
                            </Select.Group>
                        </Select.Content>
                    </Select>
                </div>
            </div>
            <div className="form__field__details__devider" />
            <span className="form__field__title">Command Specifications</span>
            <div className={`form__field__details ${formType === 'update-telecommand' ? 'disabled' : ''}`}>
                <span className="form__field__details__label">Component</span>
                <div className="w-60">
                    <Select id={1} placement="bottom" value={component} searchable={true}
                        onSearchChange={setComponentSearch}
                        onSelect={(data) => {
                            onChangeCommandData({ component: data, name: '', sequenceData: null })
                            fetchBusCommands(data)
                        }}>
                        <Select.Trigger>
                            <Select.Value value={component} placeholder={'Select Component'} />
                        </Select.Trigger>
                        <Select.Content>
                            <Select.Group>
                                {filteredComponents.map((item: any) => (
                                    <Select.Item key={item?.label} value={item?.value}>{item?.label}</Select.Item>
                                ))}
                            </Select.Group>
                        </Select.Content>
                    </Select>
                </div>
            </div>
            <div className={`form__field__details ${formType === 'update-telecommand' ? 'disabled' : ''}`}>
                <span className="form__field__details__label">Commands</span>
                <div className="w-60">
                    <Select id={1} placement="bottom" value={name} searchable={true}
                        onSearchChange={setCommandSearch}
                        onSelect={(data) => onChangeCommandData({ name: data, sequenceData: getSequenceData(data) })}>
                        <Select.Trigger disabled={!component}>
                            <Select.Value value={getCommandValue(name)} placeholder={'Select Command'} />
                        </Select.Trigger>
                        <Select.Content>
                            <Select.Group>
                                {filteredCommands.map((item: any) => (
                                    <Select.Item key={item?.label} value={item?.value?.name}>{item.label}</Select.Item>
                                ))}
                            </Select.Group>
                        </Select.Content>
                    </Select>
                </div>
            </div>
            <div className="form__field__details__devider" />

            <DualEditorForm
                jsonData={editorTelecommandData}
                setPreviewTeleCommandData={syncEditorDataToSchedule}
                setBusFormData={syncEditorDataToSchedule}
                selectedTelecommand={selectedEditorTelecommand}
                onJsonChange={syncEditorDataToSchedule}
                scrollClassName="dynamic__form__scroll__container"
                mode={formType === 'update-telecommand' ? 'update' : 'add'}
            />

            <span className="form__field__title">Scheduling Specifications</span>
            <div className={`form__field__details ${disabledSpecification ? 'disabled' : ''}`}>
                <span className="form__field__details__label">
                    Delay
                    <sup className="ms-1">
                        <CustomToolTip placement="top-end" maxWidth="220px" title="Delay is the time between end time of previous command and start time of this command.">
                            <InfoSvg />
                        </CustomToolTip>
                    </sup>
                </span>
                <div className='form__toggle__button__container'>
                    {TimeOptions.map((item) => {
                        const active = timeOption === item.name ? 'active' : ''
                        return (
                            <button key={item.name} className={`form__toggle__button ${active}`}
                                onClick={() => {
                                    let newTime = 0;
                                    newTime = calculateTimeBasedOnTimeOption({
                                        timeOption: item.name,
                                        startTime: telecommandData.commandStartTime ?? 0,
                                        endTime: telecommandData.commandEndTime ?? 0,
                                        value: Number(delay) ?? 0
                                    });
                                    onChangeCommandData({
                                        'timeOption': item.name,
                                        'time': newTime,
                                        'delay': Number(delay) ?? 10
                                    })
                                }}
                            >
                                {item.label}
                            </button>
                        )
                    })}
                </div>
            </div>
            <div className={`form__field__details ${disabledSpecification ? 'disabled' : ''}`}>
                <span className="form__field__details__label" />
                <div className='form__field__details__input__container w-40'>
                    <input type='number'
                        onKeyDown={validateNumberWithoutDecimal}
                        placeholder='Delay'
                        value={delay}
                        className='form__input'
                        onChange={(event) => {
                            const value = event.target.value;
                            if (Number(value) < 0) return;
                            const num = Number(value);
                            const maxDelaySecs = maxScheduleDuration * 60;
                            if (num > maxDelaySecs) {
                                showToastMessage('info', getInvalidRangeErrorMessage('Delay', 1, maxDelaySecs, 'secs'));
                                return;
                            }
                            let newTime = 0;
                            newTime = calculateTimeBasedOnTimeOption({
                                timeOption,
                                startTime: telecommandData.commandStartTime,
                                endTime: telecommandData.commandEndTime,
                                value: num
                            });
                            onChangeCommandData({
                                'delay': value,
                                'time': newTime
                            });
                        }}
                    />
                    <div className='input__parameter'>sec</div>
                </div>
            </div>

            <div className="form__field__details">
                <span className="form__field__details__label">Start Time</span>
                <div className="w-70 form__field__details__input__container">
                    <DatetimeInput
                        onChange={(value) => {
                            onChangeCommandData({ time: value })
                        }}
                        value={time}
                        key={'scenarioStartTime'}
                        onValidationChange={(isValid) => {
                            onDateTimeValidationChange?.(isValid);
                        }}
                    />
                </div>
            </div>
            <div className="form__field__details">
                <span className="form__field__details__label">Duration</span>
                <div className='form__field__details__input__container w-40'>
                    <input type='number'
                        onKeyDown={validateNumberWithoutDecimal}
                        placeholder='Duration'
                        value={duration}
                        className='form__input'
                        onChange={(event) => {
                            const value = event.target.value
                            handleDurationChange(value)
                        }}
                    />
                    <div className='input__parameter'>sec</div>
                </div>
            </div>
        </div>
    )
}

export default TelecommandForm
